/**
 * Music (Phase 8): two slots with an equal-power crossfade, a duck gain that dips the bed
 * under stings and blasts, and loops that start at the pipeline's silence-trimmed points.
 */
import type { FileMeta } from './library.js';

const CROSSFADE = 1.5;

interface MusicSlot {
  cueId: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
  /** When the fade-out completes; the slot is freed after it. */
  endsAt: number;
}

export class MusicPlayer {
  /** Between the music bus and the master: 1 at rest, dipped by `duck`. */
  readonly duckGain: GainNode;
  private current: MusicSlot | null = null;
  private fading: MusicSlot[] = [];

  constructor(
    private readonly ctx: AudioContext,
    private readonly bus: GainNode,
  ) {
    this.duckGain = ctx.createGain();
    this.bus.connect(this.duckGain);
  }

  /** The output the duck gain feeds (the master bus). */
  connect(destination: AudioNode): void {
    this.duckGain.connect(destination);
  }

  get playing(): string | null {
    return this.current?.cueId ?? null;
  }

  /** Starts a track, crossfading from the current one; a repeat request is a no-op. */
  play(cueId: string, buffer: AudioBuffer, meta: FileMeta | undefined, gain: number): void {
    if (this.current?.cueId === cueId) return;
    const now = this.ctx.currentTime;
    this.fadeOut(now);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.loopStart = meta?.loopStart ?? 0;
    source.loopEnd = meta?.loopEnd ?? buffer.duration;
    const slotGain = this.ctx.createGain();
    slotGain.gain.setValueAtTime(0.0001, now);
    // Equal-power in: sin ramp approximated by an exponential rise to the level.
    slotGain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain), now + CROSSFADE);
    source.connect(slotGain).connect(this.bus);
    source.start(now, source.loopStart);
    this.current = { cueId, source, gain: slotGain, endsAt: Infinity };
  }

  stop(seconds = CROSSFADE): void {
    this.fadeOut(this.ctx.currentTime, seconds);
  }

  /** Dips the music by `amount` (0..1) and lets it back up over `seconds`. */
  duck(amount: number, seconds: number): void {
    const now = this.ctx.currentTime;
    const level = Math.max(0, 1 - amount);
    this.duckGain.gain.cancelScheduledValues(now);
    this.duckGain.gain.setTargetAtTime(level, now, 0.04);
    this.duckGain.gain.setTargetAtTime(1, now + seconds, 0.4);
  }

  update(now: number): void {
    this.fading = this.fading.filter((slot) => now < slot.endsAt);
  }

  dispose(): void {
    const now = this.ctx.currentTime;
    this.fadeOut(now, 0.05);
    for (const slot of this.fading) {
      try {
        slot.source.stop(now + 0.1);
      } catch {
        // Already stopped.
      }
    }
    this.fading = [];
  }

  private fadeOut(now: number, seconds = CROSSFADE): void {
    const slot = this.current;
    if (!slot) return;
    this.current = null;
    slot.gain.gain.cancelScheduledValues(now);
    slot.gain.gain.setValueAtTime(Math.max(0.0001, slot.gain.gain.value), now);
    slot.gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds);
    slot.endsAt = now + seconds + 0.1;
    try {
      slot.source.stop(slot.endsAt);
    } catch {
      // Already stopped.
    }
    this.fading.push(slot);
  }
}
